import { useQueryParams } from '@/hooks/useQueryParams';

// Reads the `code` the Outlook OAuth / admin-consent callbacks redirect back
// with, so connect surfaces can offer the admin-consent flow (org/school
// tenants where a regular user can't self-consent to Calendars.ReadWrite) or
// acknowledge that a tenant admin has approved Agenda Hero.
// ADMIN_CONSENT_GRANTED is the only code that reports success.
const SUCCESS_CODES = ['ADMIN_CONSENT_GRANTED'];

// ADMIN_CONSENT_REQUIRED also means the connect did not complete, but it is not a
// dead end and it is not a toast: the connect surfaces render a "Get admin
// approval" CTA off `isAdminConsentRequired` (see OutlookCalendarAuthButton), so
// it is excluded here to keep a generic error from talking over that CTA. It also
// means the code must survive — clearing it removes the CTA.
const CODES_HANDLED_IN_PLACE = ['ADMIN_CONSENT_REQUIRED'];

const isFailure = (code: string | null): code is string =>
  !!code &&
  !SUCCESS_CODES.includes(code) &&
  !CODES_HANDLED_IN_PLACE.includes(code);

const FAILURE_MESSAGES: Record<string, string> = {
  PERMISSION_NOT_GRANTED:
    "Outlook access wasn't granted, so we couldn't connect your calendar.",
  INVALID_LOGIN: 'Please sign in again to connect your Outlook calendar.',
};

export const useOutlookConsentStatus = () => {
  const { queryParams, removeQueryParam } = useQueryParams();
  const code = queryParams.get('code');

  return {
    isAdminConsentRequired: code === 'ADMIN_CONSENT_REQUIRED',
    isAdminConsentGranted: code === 'ADMIN_CONSENT_GRANTED',
    // The launch param that resumes a connect flow survives the redirect whether
    // the callback succeeded or failed, so a resumed surface has to check this
    // before it announces success — otherwise a failed connect still shows a
    // "connected" toast (and the stale code rides along into the next attempt).
    connectError: isFailure(code) ? code : null,
    connectErrorMessage: isFailure(code)
      ? (FAILURE_MESSAGES[code] ??
        'Something went wrong connecting your Outlook calendar. Please try again, and let us know if it keeps happening.')
      : null,
    clearConsentCode: () => removeQueryParam('code'),
  };
};
